<script lang="ts" setup>
import { useRoute } from 'vue-router';

import { ref, useBreakpoint } from '#imports';

const route = useRoute();
const { isMobile } = useBreakpoint();
const config = useRuntimeConfig();
const isNavOpen = ref(false);

const updateIsOpen = (newState: boolean) => {
    isNavOpen.value = newState;
};

</script>

<template>
  <header aria-label="navigation with wallet and cart"
          class="header display-flex align-items-center justify-center gap-32 width-full"
  >
    <div class="container-header">
      <div class="header-content display-flex align-items-center">
        <nav class="header-nav display-flex align-items-center">
          <NuxtLink aria-label="game-changer"
                    class="display-600-accent header-logo display-flex"
                    to="/"
          >
            <IconTypes :name="config.public.logo.main.name as SvgNameKeysSlash"
                       :size="config.public.logo.main.size"
                       class="header-logo-text"
            />
            <IconTypes :name="config.public.logo.short.name as SvgNameKeysSlash"
                       :size="config.public.logo.short.size"
                       class="header-logo-text-short m-auto"
                       icon="logo-short"
            />
          </NuxtLink>

          <div class="categories-container display-none-bp-576 display-flex">
            <NuxtLink class="compact-400-accent display-flex align-items-center"
                      to="/collections"
            >
              <span :class="['header-category-text', { 'header-category-link--active': route.path === '/collection' }]"
              >
                Collections
              </span>
            </Nuxtlink>

            <NuxtLink :to="{ name: 'drops', query: { status: Status.live } }"
                      class="compact-400-accent display-flex align-items-center"
            >
              <span :class="['header-category-text', { 'header-category-link--active': route.path === '/drops' }]">
                Drops
              </span>
            </NuxtLink>

            <Dropdown :is-open="isNavOpen"
                      class="categories-container__dropdown display-none-bp-576 align-items-center m-auto"
                      position="bottomRight"
                      @update:is-open="(val: boolean) => updateIsOpen(val)"
            >
              <template #toggle>
                <div :class="isNavOpen && 'categories-button--active'"
                     aria-label="categories"
                     class="categories-button display-flex justify-center align-items-center cursor-pointer"
                     role="button"
                     type="button"
                     @click="isNavOpen = !isNavOpen"
                >
                  <IconMain :size="24"
                            icon="grid"
                  />
                </div>
              </template>
              <template #content>
                <div class="categories-menu m-auto">
                  <NuxtLink :to="`/collections`"
                            aria-label="Collections"
                            class="display-flex align-items-center gap-8 link categories-menu__link"
                  >
                    <IconMain :size="24"
                              icon="grid"
                    />
                    <p class="compact-500-accent">
                      Collections
                    </p>
                  </NuxtLink>

                  <NuxtLink :to="`/drops`"
                            aria-label="Drops"
                            class="display-flex align-items-center gap-8 link categories-menu__link"
                  >
                    <IconMain :size="24"
                              icon="falling-star"
                    />
                    <p class="compact-500-accent">
                      Drops
                    </p>
                  </NuxtLink>
                </div>
              </template>
            </Dropdown>
          </div>

        </nav>
        <div class="header-search display-flex align-items-center">
          <ClientOnly>
            <template #fallback>
              <SearchInput aria-label="search"
                           class="pointer-events-none width-full"
              />
            </template>
            <template #default>
              <HeaderSearch :is-mobile="isMobile" />
            </template>
          </ClientOnly>

          <div class="header_buttons display-flex align-items-center gap-10 gap-0-bp-576">

            <ClientTransition>
              <template #fallback>
                <div class="display-none-bp-576">
                  <LoaderSpinner :size="52">
                    <IconMain :size="23"
                              icon="wallet"
                    />
                  </LoaderSpinner>
                </div>
              </template>
              <div class="align-self-stretch">
                <Web3WalletButton :is-mobile="isMobile" />
              </div>
            </ClientTransition>

            <ClientTransition>
              <template #fallback>
                <LoaderSpinner :size="52"
                               class="display-none-bp-576">
                  <IconTypes :size="23"
                             name="icon/chain"  />
                </LoaderSpinner>
              </template>
              <div class="align-self-stretch">
                <Web3ChainButton />
              </div>
            </ClientTransition>

            <ClientOnly>
              <template #fallback>
                <div class="header-client__cart display-flex justify-center align-items-center display-none-bp-576">
                  <IconMain :size="24"
                            icon="sun"
                  />
                </div>
              </template>
              <ThemeSwitcher class="display-none-bp-576" />
            </ClientOnly>

            <ClientOnly>
              <template #fallback>
                <div class="header-client__cart display-flex justify-center align-items-center">
                  <IconMain :size="24"
                            icon="cart"
                  />
                </div>
              </template>
              <template #default>
                <CartButton :is-mobile="isMobile" />
              </template>
            </ClientOnly>

            <ClientOnly>
              <template #fallback>
                <div class="btn-menu header-menu__icon justify-center items-center">
                  <IconMain :size="24"
                            icon="menu"
                  />
                </div>
              </template>
              <HeaderMenu :is-mobile="isMobile" />
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
    <ClientOnly>
      <ConnectModal />
    </ClientOnly>
  </header>
</template>

<style lang="scss" scoped>
.header {
  position: fixed;
  z-index: 200;
  background: var(--layer-floor-1);

  &-client {
    &__cart {
      width: var(--control-500-size-single, 52px);
      height: var(--control-500-size-single, 52px);
    }
  }

  &-menu {
    &__icon {
      display: none;

      @include mobile-576 {
        display: flex;
      }
    }
  }
}

.header-nav {
  gap: 40px;
  width: 25%;
  height: 52px;
}

.header-logo {
  color: var(--content-primary);
}

.header-search {
  gap: var(--grid-x-large, 40px);
  flex: 1 0 0;
  padding: 0 16px;

  @include mobile-576 {
    gap: 8px;
    padding: 0 0 0 16px;
  }
}

.header-category-text,
.header-drops-text {
  transition: opacity 0.3s ease;
  color: var(--content-primary);

  &:hover {
    opacity: 0.4;
  }
}

.header-category-link {
  &--active {
    color: var(--content-brand-mojito-active-link);
  }
}
//
//.header-logo-text {
//  width: 100px;
//  height: 52px;
//}

.header-logo-text-short {
  display: none;
}

.categories-container {
  height: 100%;
  gap: 40px;

  &__dropdown {
    display: none;
  }
}

.categories-menu {
  width: 167px;
  padding: $spacing-400 $spacing-600;
  border-radius: var(--background-500-radius, 4px);
  background-color: var(--layer-floor-2);

  &__link {
    padding: 14px 0;
  }
}

@include desktop-1920 {
  .categories-container {
    gap: 40px;
  }
}

@include desktop-1440 {
  .categories-container {
    gap: 20px;
  }

  .header-nav {
    gap: 40px;
  }
}

@include desktop-1200 {
  .header-logo-text {
    display: none;
  }

  .header-logo-text-short {
    display: inline;
  }

  .categories-container {
    width: 52px;
    height: 52px;
    margin: auto;

    & > a {
      display: none;
    }

    &__dropdown {
      display: block;
    }
  }

  .header-category-text {
    display: none;
  }

  .header-drops-text {
    display: none;
  }

  .header-nav {
    justify-content: space-between;
    width: 17%;
    min-width: 68px;
  }
}

@include tablet-768 {
  .header-nav {
    width: auto;
    gap: 20px;
  }
}

.btn-menu {
  width: 52px;
}

.file-icon-text {
  position: absolute;
  bottom: -34px;
  left: 25px;
  padding: 5px 10px;
  transform: translateX(-50%);
  transition: opacity 0.3s ease;
  border-radius: var(--radius-999, 999px);
  opacity: 0;
  background: var(--bg-brand-aviation-secondary);
  color: var(--content-primary);
  font-size: 14px;
}

.file-icon-container {
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;

  svg {
    color: var(--content-primary);
  }
}

.file-icon-container:hover .file-icon-text {
  opacity: 1;
}
</style>
